import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from "recharts";
import { Refresh } from "./ui/Icons";

const API = "https://agrihyphen-ai.onrender.com";

/* Chart tokens, pulled from the same palette as the rest of the site so the
   dashboard doesn't read as a different product. */
const INK = "#14180F";
const RULE = "#DED8C9";
const MUTED = "#6E7565";
const SERIES = {
  daily: "#2C6B41",
  weekly: "#A8482A",
  monthly: "#13301D",
};

const axisProps = {
  stroke: RULE,
  tick: {
    fill: MUTED,
    fontSize: 11,
    fontFamily: "'JetBrains Mono', monospace",
  },
  tickLine: false,
  axisLine: { stroke: RULE },
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-agri-rule bg-agri-card px-3 py-2 shadow-raise">
      <p className="font-mono text-[0.625rem] uppercase tracking-[0.1em] text-agri-ink40">
        {label}
      </p>
      <p className="mt-1 font-display text-[1.125rem] font-semibold tabular-nums text-agri-ink">
        {payload[0].value}
        <span className="ml-1.5 font-sans text-[0.75rem] font-normal text-agri-ink60">
          requests
        </span>
      </p>
    </div>
  );
};

const Panel = ({ title, note, children, className = "" }) => (
  <section className={`border border-agri-rule bg-agri-card ${className}`}>
    <header className="flex items-baseline justify-between gap-4 border-b border-agri-rule px-5 py-4">
      <h2 className="font-display text-[1.0625rem] font-semibold tracking-[-0.015em] text-agri-ink">
        {title}
      </h2>
      {note && (
        <p className="font-mono text-[0.625rem] uppercase tracking-[0.1em] text-agri-ink40">
          {note}
        </p>
      )}
    </header>
    <div className="px-2 py-5 sm:px-4">{children}</div>
  </section>
);

const SummaryTile = ({ label, value, period, accent }) => (
  <div className="border-t-2 pt-5" style={{ borderTopColor: accent }}>
    <p className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-agri-ink40">
      {label}
    </p>
    <p className="figure mt-3 text-[2.5rem] leading-none text-agri-ink sm:text-[3rem]">
      {value}
    </p>
    <p className="mt-3 text-[0.8125rem] tabular-nums text-agri-ink60">
      {period}
    </p>
  </div>
);

const EmptyChart = ({ message }) => (
  <div className="flex h-[260px] items-center justify-center border border-dashed border-agri-rule">
    <p className="text-[0.875rem] text-agri-ink40">{message}</p>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    daily: null,
    weekly: null,
    monthly: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const start = dateRange.start || undefined;
      const end = dateRange.end || undefined;

      const [dailyRes, weeklyRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/stats/daily`, {
          params: { start_date: start, end_date: end },
        }),
        axios.get(`${API}/weekly`, {
          params: { start_date: start, end_date: end },
        }),
        axios.get(`${API}/monthly`, {
          params: {
            start_date: start?.slice(0, 7),
            end_date: end?.slice(0, 7),
          },
        }),
      ]);

      setStats({
        daily: dailyRes.data,
        weekly: weeklyRes.data,
        monthly: monthlyRes.data,
      });
    } catch (err) {
      console.error("Error fetching stats:", err);
      setError(
        "Could not load statistics. The analytics service may be waking up — try again in a moment."
      );
    } finally {
      setLoading(false);
    }
  }, [dateRange.start, dateRange.end]);

  useEffect(() => {
    fetchStats();
    // Initial load only; subsequent loads are driven by the Update button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatData = (data, type) => {
    const rows = data?.[`${type}_stats`];
    if (!Array.isArray(rows)) return [];
    return rows.map(({ date, count }) => ({ date, count }));
  };

  const period = (block) =>
    block?.period?.start
      ? `${block.period.start} → ${block.period.end}`
      : "All time";

  const dailyData = formatData(stats.daily, "daily");
  const weeklyData = formatData(stats.weekly, "weekly");
  const monthlyData = formatData(stats.monthly, "monthly");

  return (
    <main className="min-h-screen bg-agri-paper">
      <header className="border-b border-agri-rule bg-agri-card">
        <div className="container-x page-top pb-10">
          <p className="kicker-rule">Internal</p>
          <div className="mt-6 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="font-display text-h2 font-semibold leading-[1.05] tracking-[-0.026em] text-agri-ink sm:text-h2Lg">
                Usage analytics
              </h1>
              <p className="mt-4 max-w-measure text-[0.9375rem] leading-[1.7] text-agri-ink80">
                Prediction requests served by the AgriHyphen AI model endpoint.
              </p>
            </div>

            {/* Filter bar — labels are visible, not placeholder-only */}
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                fetchStats();
              }}
            >
              {[
                { id: "start", label: "From" },
                { id: "end", label: "To" },
              ].map((f) => (
                <div key={f.id} className="flex flex-col gap-1.5">
                  <label
                    htmlFor={`range-${f.id}`}
                    className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-agri-ink40"
                  >
                    {f.label}
                  </label>
                  <input
                    id={`range-${f.id}`}
                    type="date"
                    value={dateRange[f.id]}
                    onChange={(e) =>
                      setDateRange((p) => ({ ...p, [f.id]: e.target.value }))
                    }
                    className="h-11 border border-agri-rule bg-agri-paper px-3 font-mono text-[0.8125rem] text-agri-ink transition-colors focus:border-agri-ink"
                  />
                </div>
              ))}
              <button type="submit" className="btn-primary" disabled={loading}>
                <Refresh
                  className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                />
                {loading ? "Loading" : "Update"}
              </button>
            </form>
          </div>
        </div>
      </header>

      <div className="container-x section-tight">
        {error && (
          <div
            role="alert"
            className="mb-10 flex flex-col gap-3 border-l-2 border-agri-clay bg-agri-card p-5 sm:flex-row sm:items-center sm:justify-between"
          >
            <p className="text-[0.9375rem] text-agri-ink">{error}</p>
            <button
              onClick={fetchStats}
              className="btn-outline shrink-0 !min-h-[2.5rem]"
            >
              Retry
            </button>
          </div>
        )}

        {/* Summary figures */}
        <dl className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-3">
          <SummaryTile
            label="Requests · daily"
            value={
              loading ? "—" : (stats.daily?.total_requests ?? 0).toLocaleString()
            }
            period={period(stats.daily)}
            accent={SERIES.daily}
          />
          <SummaryTile
            label="Requests · weekly"
            value={
              loading
                ? "—"
                : (stats.weekly?.total_requests ?? 0).toLocaleString()
            }
            period={period(stats.weekly)}
            accent={SERIES.weekly}
          />
          <SummaryTile
            label="Requests · monthly"
            value={
              loading
                ? "—"
                : (stats.monthly?.total_requests ?? 0).toLocaleString()
            }
            period={period(stats.monthly)}
            accent={SERIES.monthly}
          />
        </dl>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Panel title="Daily trend" note="Requests per day">
            {dailyData.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={dailyData}
                  margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="dailyFill" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={SERIES.daily}
                        stopOpacity={0.18}
                      />
                      <stop
                        offset="100%"
                        stopColor={SERIES.daily}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={RULE} vertical={false} />
                  <XAxis dataKey="date" {...axisProps} />
                  <YAxis {...axisProps} width={44} allowDecimals={false} />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ stroke: INK, strokeOpacity: 0.2 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    name="Requests"
                    stroke={SERIES.daily}
                    strokeWidth={2}
                    fill="url(#dailyFill)"
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart
                message={loading ? "Loading…" : "No requests in this range yet"}
              />
            )}
          </Panel>

          <Panel title="Weekly distribution" note="Requests per week">
            {weeklyData.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={weeklyData}
                  margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
                >
                  <CartesianGrid stroke={RULE} vertical={false} />
                  <XAxis dataKey="date" {...axisProps} />
                  <YAxis {...axisProps} width={44} allowDecimals={false} />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ fill: INK, fillOpacity: 0.04 }}
                  />
                  <Bar
                    dataKey="count"
                    name="Requests"
                    fill={SERIES.weekly}
                    maxBarSize={38}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart
                message={loading ? "Loading…" : "No requests in this range yet"}
              />
            )}
          </Panel>

          <Panel title="Monthly overview" note="Requests per month" className="lg:col-span-2">
            {monthlyData.length ? (
              <ResponsiveContainer width="100%" height={340}>
                <LineChart
                  data={monthlyData}
                  margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
                >
                  <CartesianGrid stroke={RULE} vertical={false} />
                  <XAxis dataKey="date" {...axisProps} />
                  <YAxis {...axisProps} width={44} allowDecimals={false} />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ stroke: INK, strokeOpacity: 0.2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="Requests"
                    stroke={SERIES.monthly}
                    strokeWidth={2}
                    dot={{ r: 3, fill: SERIES.monthly, strokeWidth: 0 }}
                    activeDot={{ r: 5, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart
                message={loading ? "Loading…" : "No requests in this range yet"}
              />
            )}
          </Panel>
        </div>
      </div>
    </main>
  );
};

export default Dashboard;
